import React from 'react'
import { ChevronRight } from 'lucide-react'

interface QuoteStageProgressProps {
  currentStage: string
  onStageChange: (newStage: string) => void
  disabled?: boolean
}

// Salesforce quote stages in exact order from screenshot
const QUOTE_STAGES = [
  'Draft',
  'Needs Review',
  'In Review',
  'Approved',
  'Rejected',
  'Presented',
  'Final Review',
  'Accepted',
  'Denied',
  'Processed',
] as const

type QuoteStage = (typeof QUOTE_STAGES)[number]

// Categories for color coding
const SUCCESS_STAGES: QuoteStage[] = ['Approved', 'Accepted', 'Processed']
const REJECTED_STAGES: QuoteStage[] = ['Rejected', 'Denied']

export function QuoteStageProgress({
  currentStage,
  onStageChange,
  disabled = false,
}: QuoteStageProgressProps) {
  const currentIndex = QUOTE_STAGES.indexOf(currentStage as QuoteStage)

  const handleStageClick = (stage: QuoteStage) => {
    if (disabled || stage === currentStage) return
    onStageChange(stage)
  }

  const handleKeyDown = (e: React.KeyboardEvent, stage: QuoteStage) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleStageClick(stage)
    }
  }

  const getStageStyles = (stage: QuoteStage, index: number): string => {
    const isActive = stage === currentStage
    const isPast = currentIndex > index
    const canClick = !disabled && stage !== currentStage

    if (isActive) {
      // Active stage - ring highlight with subtle background (matching Opportunity style)
      if (SUCCESS_STAGES.includes(stage)) {
        return 'bg-accent/10 text-accent border-accent ring-2 ring-accent'
      }
      if (REJECTED_STAGES.includes(stage)) {
        return 'bg-destructive/10 text-destructive border-destructive ring-2 ring-destructive'
      }
      // Draft and review stages
      return 'bg-primary/10 text-primary border-primary ring-2 ring-primary'
    }

    if (isPast) {
      // Completed stages - subtle fill
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    // Future stages - clickable
    if (canClick) {
      return 'bg-white text-primary border-primary/30 hover:bg-primary/5 cursor-pointer'
    }

    return 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
  }

  return (
    <nav
      className="bg-white border rounded-lg p-4 shadow-sm"
      aria-label="Quote stage progression"
      role="navigation"
    >
      <div className="flex items-center justify-start gap-2 flex-wrap">
        {QUOTE_STAGES.map((stage, index) => {
          const isActive = stage === currentStage
          const canClick = !disabled && stage !== currentStage

          return (
            <React.Fragment key={stage}>
              <button
                onClick={() => handleStageClick(stage)}
                onKeyDown={(e) => handleKeyDown(e, stage)}
                disabled={!canClick}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
                  ${getStageStyles(stage, index)}
                `}
                aria-label={`${stage} stage${isActive ? ' (current)' : ''}${canClick ? ' - click to change' : ''}`}
                aria-current={isActive ? 'step' : undefined}
                tabIndex={canClick ? 0 : -1}
              >
                <span className="font-medium">{stage}</span>
              </button>
              {index < QUOTE_STAGES.length - 1 && (
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Screen reader context */}
      <div className="sr-only">
        Quote stage progression. Current stage: {currentStage}. Click any stage to change the quote
        status.
      </div>
    </nav>
  )
}
